// Copyright © 2019-2026 Focal Systems. All rights reserved.

import { BadgeCheck, BadgeMinus, MoreHorizontal, Power, PowerOff } from "lucide-react";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { useAuth } from "@/lib/auth/auth-provider";
import { PAGE_PERMISSIONS } from "@/lib/auth/page-permissions";
import { cn } from "@/lib/utils";

interface FirmwareActionsMenuProps {
  isActive: boolean;
  isProduction: boolean;
  onToggleActive: () => void;
  onToggleProduction: () => void;
  triggerClassName?: string;
}

export function FirmwareActionsMenu({
  isActive,
  isProduction,
  onToggleActive,
  onToggleProduction,
  triggerClassName,
}: FirmwareActionsMenuProps) {
  const { t } = useTranslation("back-office");
  const { hasPermission } = useAuth();
  if (!hasPermission(PAGE_PERMISSIONS.hardwareFirmwareEdit)) return null;

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="ghost" size="icon" className={cn("h-8 w-8", triggerClassName)}>
          <MoreHorizontal className="h-4 w-4" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" sideOffset={-32} className="dropdown-overlap">
        <DropdownMenuItem
          onSelect={onToggleProduction}
          className={isProduction ? "text-destructive focus:text-destructive" : ""}
        >
          {isProduction ? <BadgeMinus className="h-4 w-4 text-destructive" /> : <BadgeCheck className="h-4 w-4" />}
          {isProduction ? t("firmware.actions.unmarkProduction") : t("firmware.actions.markProduction")}
        </DropdownMenuItem>
        <DropdownMenuSeparator />
        <DropdownMenuItem
          onSelect={onToggleActive}
          className={isActive ? "text-destructive focus:text-destructive" : ""}
        >
          {isActive ? (
            <>
              <PowerOff className="h-4 w-4 text-destructive" />
              {t("firmware.actions.deactivate")}
            </>
          ) : (
            <>
              <Power className="h-4 w-4" />
              {t("firmware.actions.activate")}
            </>
          )}
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
